import React, { useEffect, useRef, useState } from "react";
import "../Contactus/Contactus.css";
import { FaPhoneAlt } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { GoLocation } from "react-icons/go";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ─── FAQ Data ─── */
const FAQ_DATA = [
  {
    q: "How do I schedule an initial consultation?",
    a: "You can reach us by phone, email, or by submitting the contact form on this page. Our team typically responds within one business day to confirm your appointment.",
  },
  {
    q: "What information should I bring to my first meeting?",
    a: "Bring any relevant documents related to your legal matter — contracts, notices, correspondence, or identification documents. The more context you provide, the more effectively our attorneys can advise you.",
  },
  {
    q: "Do you offer virtual consultations?",
    a: "Yes, we offer video and phone consultations for clients who are unable to visit in person. Please mention your preference when you reach out.",
  },
  {
    q: "What are your consultation fees?",
    a: "Consultation fees vary by practice area and case complexity. Please contact our office directly for a transparent quote — we believe in clear, upfront pricing.",
  },
  {
    q: "How quickly can I expect a response to my inquiry?",
    a: "We aim to respond to all inquiries within 24 business hours. Urgent matters can be flagged as such in your message, and we will prioritise accordingly.",
  },
];

/* ─── Working Hours Data ─── */
const HOURS_DATA = [
  { day: "Monday – Friday", time: "9:00 AM – 7:00 PM", open: true },
  { day: "Saturday", time: "10:00 AM – 4:00 PM", open: true },
  { day: "Sunday", time: "Closed", open: false },
  { day: "Public Holidays", time: "By Appointment Only", open: true },
];

/* ─── Quick Facts ─── */
const FACTS = [
  { num: "15+", label: "Years of Practice" },
  { num: "98%", label: "Client Satisfaction" },
  { num: "1200+", label: "Cases Resolved" },
  { num: "24hr", label: "Response Time" },
];

/* ============================================================
   COMPONENT
   ============================================================ */
const ContactUs = () => {
  /* FAQ state */
  const [openFaq, setOpenFaq] = useState(null);
  const faqBodyRefs = useRef([]);

  /* Form state */
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    consent: false,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ─── GSAP Refs ─── */
  const heroRef = useRef(null);
  const fadedRef = useRef(null);
  const lineRef = useRef(null);
  const subRef = useRef(null);
  const headingRef = useRef(null);
  const bannerRef = useRef(null);
  const infoCardsRef = useRef([]);
  const formLeftRef = useRef(null);
  const formRightRef = useRef(null);
  const hoursRef = useRef(null);
  const factsRef = useRef(null);
  const faqHeaderRef = useRef(null);
  const faqItemsRef = useRef([]);
  const ctaRef = useRef(null);

  /* ─── GSAP Animations ─── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Hero entrance */
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" } });

      heroTl
        .from(fadedRef.current, {
          opacity: 0,
          scale: 1.2,
          duration: 1.4,
          ease: "power2.out",
        })
        .from(
          lineRef.current,
          { scaleX: 0, duration: 0.6, ease: "power2.out" },
          "-=0.8",
        )
        .from(subRef.current, { opacity: 0, y: 20, duration: 0.55 }, "-=0.3")
        .from(
          headingRef.current,
          { opacity: 0, y: 30, duration: 0.65 },
          "-=0.35",
        );

      /* Banner */
      gsap.from(bannerRef.current, {
        opacity: 0,
        y: 40,
        scale: 0.97,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: bannerRef.current,
          start: "top 88%",
        },
      });

      /* Info cards — stagger */
      gsap.from(infoCardsRef.current, {
        opacity: 0,
        y: 50,
        stagger: 0.14,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: infoCardsRef.current[0],
          start: "top 88%",
        },
      });

      /* Form left */
      gsap.from(formLeftRef.current, {
        opacity: 0,
        x: -50,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: formLeftRef.current,
          start: "top 85%",
        },
      });

      /* Form right */
      gsap.from(formRightRef.current, {
        opacity: 0,
        x: 50,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: formRightRef.current,
          start: "top 85%",
        },
      });

      /* Hours table rows */
      gsap.from(hoursRef.current, {
        opacity: 0,
        x: -40,
        duration: 0.75,
        ease: "power3.out",
        scrollTrigger: {
          trigger: hoursRef.current,
          start: "top 88%",
        },
      });

      /* Fact cards */
      gsap.from(factsRef.current?.querySelectorAll(".ct-fact-card") ?? [], {
        opacity: 0,
        scale: 0.85,
        stagger: 0.1,
        duration: 0.6,
        ease: "back.out(1.5)",
        scrollTrigger: {
          trigger: factsRef.current,
          start: "top 88%",
        },
      });

      /* FAQ header */
      gsap.from(faqHeaderRef.current, {
        opacity: 0,
        y: 30,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: faqHeaderRef.current,
          start: "top 88%",
        },
      });

      /* FAQ items stagger */
      gsap.from(faqItemsRef.current, {
        opacity: 0,
        y: 24,
        stagger: 0.1,
        duration: 0.55,
        ease: "power2.out",
        scrollTrigger: {
          trigger: faqItemsRef.current[0],
          start: "top 90%",
        },
      });

      /* CTA section */
      gsap.from(
        ctaRef.current?.querySelectorAll(
          ".ct-cta-title, .ct-cta-desc, .ct-cta-btns",
        ) ?? [],
        {
          opacity: 0,
          y: 30,
          stagger: 0.15,
          duration: 0.65,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 85%",
          },
        },
      );
    });

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  /* ─── FAQ accordion ─── */
  const toggleFaq = (index) => {
    const isOpen = openFaq === index;
    setOpenFaq(isOpen ? null : index);

    faqBodyRefs.current.forEach((el, i) => {
      if (!el) return;
      if (i === index) {
        el.style.maxHeight = isOpen ? "0px" : el.scrollHeight + "px";
      } else {
        el.style.maxHeight = "0px";
      }
    });
  };

  /* ─── Form handlers ─── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    /* Simulate async submission */
    setTimeout(() => {
      setIsSubmitting(false);
      setFormSubmitted(true);
    }, 1400);
  };

  /* ─── Icon hover micro-animation ─── */
  const handleCardEnter = (e) => {
    const icon = e.currentTarget.querySelector(".ct-card-icon-wrap");
    gsap.to(icon, { rotation: -5, scale: 1.1, duration: 0.25 });
  };
  const handleCardLeave = (e) => {
    const icon = e.currentTarget.querySelector(".ct-card-icon-wrap");
    gsap.to(icon, { rotation: 0, scale: 1, duration: 0.25 });
  };

  return (
    <div className="ct-page">
      {/* ==============================
          SECTION 1 — HERO
          ============================== */}
      <section className="ct-hero" ref={heroRef}>
        <div className="ct-faded-bg" ref={fadedRef}>
          CONTACT US
        </div>
        <div className="ct-hero-inner">
          <div className="ct-line" ref={lineRef} />
          <p className="ct-subheading" ref={subRef}>
            READY TO ASSIST
          </p>
          <h2 className="ct-main-heading" ref={headingRef}>
            Discuss Your Legal Needs
          </h2>
        </div>
      </section>

      {/* ==============================
          SECTION 2 — BANNER
          ============================== */}
      <div className="ct-banner-section">
        <div className="ct-banner-placeholder" ref={bannerRef}>
          <div className="ct-banner-quote">
            Trusted legal counsel,
            <br />
            <span>available when you need it most.</span>
          </div>
        </div>
        {/*
          Replace above placeholder with real image:
          <img src="/contact.webp" alt="Contact" className="ct-banner-img" ref={bannerRef} />
        */}
      </div>

      {/* ==============================
          SECTION 3 — CONTACT INFO CARDS
          ============================== */}
      <div className="ct-info-section">
        <div className="ct-cards-grid">
          {[
            {
              Icon: FaPhoneAlt,
              heading: "Phone",
              text: "Reach our legal desk directly for swift consultations or urgent case inquiries.",
              link: "+91-98111-22334",
              href: "tel:+919811122334",
            },
            {
              Icon: MdEmail,
              heading: "Email",
              text: "Send us your case brief or legal query — our team will respond with diligence.",
              link: "lawyerfirm@noida.in",
              href: "mailto:lawyerfirm@noida.in",
            },
            {
              Icon: GoLocation,
              heading: "Location",
              text: "D-42, 3rd Floor, Defence Colony, Noida – 110424, India",
              link: "Find Us on Google Maps",
              href: "https://maps.google.com",
            },
          ].map(({ Icon, heading, text, link, href }, i) => (
            <div
              key={heading}
              className="ct-info-card"
              ref={(el) => (infoCardsRef.current[i] = el)}
              onMouseEnter={handleCardEnter}
              onMouseLeave={handleCardLeave}
            >
              <div className="ct-card-icon-wrap">
                <Icon />
              </div>
              <h2 className="ct-card-heading">{heading}</h2>
              <p className="ct-card-text">{text}</p>
              <a href={href} className="ct-card-link">
                &#8594;&nbsp;{link}
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* ==============================
          SECTION 4 — CONTACT FORM
          ============================== */}
      <section className="ct-form-section">
        <div className="ct-form-container">
          {/* Left: copy */}
          <div className="ct-form-left" ref={formLeftRef}>
            <div className="ct-line" />
            <p className="ct-form-sub">SEND A MESSAGE</p>
            <h2 className="ct-form-title">
              Let Us Know
              <br />
              How We Can Help
            </h2>
            <p className="ct-form-desc">
              Whether you have a straightforward query or a complex legal
              matter, our team is ready to listen. Fill in the form and we will
              be in touch promptly. We are committed to providing clear
              guidance, professional support, and personalized legal solutions
              tailored to your needs. Your concerns matter to us, and our
              experienced team is here to assist you with dedication, and care
              throughout every step of the process.
            </p>
            <ul className="ct-assurance-list">
              {[
                "Strict confidentiality on all enquiries",
                "No obligation initial consultation",
                "Response within one business day",
                "Dedicated attorney assigned to your case",
              ].map((item) => (
                <li key={item}>
                  <span className="ct-assurance-dot">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form */}
          <div className="ct-form-right" ref={formRightRef}>
            <div className="ct-form">
              {!formSubmitted ? (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="ct-form-row">
                    <div className="ct-field">
                      <label className="ct-label" htmlFor="firstName">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="Arjun"
                        className="ct-input"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ct-field">
                      <label className="ct-label" htmlFor="lastName">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Sharma"
                        className="ct-input"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="ct-form-row">
                    <div className="ct-field">
                      <label className="ct-label" htmlFor="email">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="arjun@email.com"
                        className="ct-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ct-field">
                      <label className="ct-label" htmlFor="phone">
                        Phone Number
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+91 98765 43210"
                        className="ct-input"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div
                    className="ct-form-row"
                    style={{ marginBottom: "1.25rem" }}
                  >
                    <div className="ct-field full">
                      <label className="ct-label" htmlFor="service">
                        Area of Legal Service
                      </label>
                      <div className="ct-select-wrap">
                        <select
                          id="service"
                          name="service"
                          className="ct-select"
                          value={formData.service}
                          onChange={handleChange}
                        >
                          <option value="">Select a practice area…</option>
                          <option>Corporate & Business Law</option>
                          <option>Civil Litigation</option>
                          <option>Criminal Defence</option>
                          <option>Family & Matrimonial Law</option>
                          <option>Property & Real Estate</option>
                          <option>Intellectual Property</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="ct-field" style={{ marginBottom: 0 }}>
                    <label className="ct-label" htmlFor="message">
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Briefly describe your legal matter or question…"
                      className="ct-textarea"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="ct-checkbox-row">
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      className="ct-checkbox"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="consent" className="ct-checkbox-label">
                      I agree to the{" "}
                      <a href="/privacy-policy">Privacy Policy</a> and consent
                      to being contacted by the firm regarding my inquiry.
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="ct-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending…" : "Send Your Enquiry"}
                    {!isSubmitting && (
                      <span className="btn-arrow">&#8594;</span>
                    )}
                  </button>
                </form>
              ) : (
                <div className="ct-form-success show">
                  <div className="ct-success-icon">✓</div>
                  <h3 className="ct-success-title">Message Received</h3>
                  <p className="ct-success-text">
                    Thank you for reaching out. A member of our legal team will
                    contact you within one business day.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 5 — HOURS & FACTS
          ============================== */}
      <section className="ct-hours-section">
        <div className="ct-hours-inner">
          {/* Working Hours */}
          <div>
            <p className="ct-section-label">AVAILABILITY</p>
            <h2 className="ct-section-title">Working Hours</h2>
            <table className="ct-hours-table" ref={hoursRef}>
              <tbody>
                {HOURS_DATA.map(({ day, time, open }) => (
                  <tr key={day}>
                    <td>{day}</td>
                    <td className={!open ? "closed" : ""}>
                      {time}
                      {open &&
                        time.includes("AM") &&
                        day.includes("Monday") && (
                          <span className="ct-hours-badge">Open Now</span>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Quick Facts */}
          <div>
            <p className="ct-section-label">BY THE NUMBERS</p>
            <h2 className="ct-section-title">Why Clients Trust Us</h2>
            <div className="ct-facts-grid" ref={factsRef}>
              {FACTS.map(({ num, label }) => (
                <div className="ct-fact-card" key={label}>
                  <div className="ct-fact-num">{num}</div>
                  <div className="ct-fact-label">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 6 — FAQ ACCORDION
          ============================== */}
      <section className="ct-faq-section">
        <div className="ct-faq-container">
          <div className="ct-faq-header" ref={faqHeaderRef}>
            <div className="ct-line" />
            <p className="ct-subheading">FREQUENTLY ASKED</p>
            <h2 className="ct-section-title">Common Questions</h2>
          </div>

          <div className="ct-faq-list">
            {FAQ_DATA.map((item, i) => (
              <div
                key={i}
                className={`ct-faq-item${openFaq === i ? " open" : ""}`}
                ref={(el) => (faqItemsRef.current[i] = el)}
              >
                <button
                  className="ct-faq-trigger"
                  onClick={() => toggleFaq(i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="ct-faq-q">{item.q}</span>
                  <span className="ct-faq-icon" aria-hidden="true">
                    +
                  </span>
                </button>
                <div
                  className="ct-faq-body"
                  ref={(el) => (faqBodyRefs.current[i] = el)}
                >
                  <div className="ct-faq-body-inner">{item.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          SECTION 7 — CTA BAND
          ============================== */}
      <section className="ct-cta-section" ref={ctaRef}>
        <div className="ct-cta-inner">
          <p className="ct-cta-sub">GET IN TOUCH</p>
          <h2 className="ct-cta-title">Ready to Take the Next Step?</h2>
          <p className="ct-cta-desc">
            Our team of experienced attorneys is here to guide you through every
            stage of your legal journey. Schedule a confidential consultation
            today.
          </p>
          <div className="ct-cta-btns">
            <button className="ct-btn-primary">Book a Consultation</button>
            <button className="ct-btn-outline">
              Explore Our Practice Areas
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
